import React from 'react';
import {
  Keyboard,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import ActionsFactory from 'src/actions/ActionsFactory';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import BackSubheader from 'src/components/elements/BackSubheader';
import Localized from 'src/constants/AppStrings';
import {authStore} from 'src/init';
import Events from 'src/logging/Events';
import {getMobileAuth0Client, getWebAuth0Client} from 'src/nativeModules/Auth0';
import DeviceInfo from 'src/nativeModules/DeviceInfo';
import OneSignal from 'src/nativeModules/OneSignal';
import uuid from 'src/nativeModules/UUID';
import {store} from 'src/redux/store';
import DealRepository from 'src/services/aws/DealRepository';
import PersistentStore from 'src/services/PersistentStoreService';
import SyncHelper from 'src/services/SyncService';
import Settings from 'src/Settings';
import AccountStore from 'src/stores/AccountStore';
import type {ScreenProps} from 'src/types/Screen';
import type {SnackAction} from 'src/types/Snack';
import KeyboardAvoidingView from '../elements/365KeyboardAvoidingView';
import AVText from '../elements/AVText';
import CreateAccountPrivacyPolicyHandler from '../elements/createAccount/CreateAccountPrivacyPolicyHandler';
import DeepLinkHandler from '../elements/DeepLinkHandler';
import FocusAwareStatusBar from '../elements/FocusAwareStatusBar';
import Logo from '../elements/Logo';
import RoundedButton, {ButtonType} from '../elements/RoundedButton';
import SafeAreaView from '../elements/SafeAreaView';
import UIManager from '../elements/ui/UIManager';
import {alertError} from '../helpers/AlertHelper';
import ScreenContext from '../ScreenContext';
import Styles from '../Styles';

type WelcomeAuth0Props = {
  snack?: SnackAction;
};

type WelcomeAuth0State = {
  fullVersion: string;
};

const backgroundColor = Platform.select({
  web: 'white',
  default: Styles.loginBackground,
});

const textColor = Platform.select({
  web: 'black',
  default: Styles.loginText,
});

export class WelcomeAuth0Screen extends React.Component<
  WelcomeAuth0Props,
  WelcomeAuth0State
> {
  privacyHandler: CreateAccountPrivacyPolicyHandler | null;
  static contextType = ScreenContext;

  constructor(props: WelcomeAuth0Props) {
    super(props);
    this.state = {
      fullVersion: '',
    };
    this.checkIfLoggedIn = this.checkIfLoggedIn.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleLoginAVLive = this.handleLoginAVLive.bind(this);
    this.webLoginAuth0 = this.webLoginAuth0.bind(this);
  }

  async componentDidMount() {
    const loggedIn = await this.checkIfLoggedIn();

    if (!loggedIn) {
      const fullVersion = await Settings.getFullDisplayVersion(Platform.OS);
      this.setState({
        fullVersion,
      });
    }
  }

  handleLoginAVLive() {
    NavActions.push(AppRoutes.Login);
  }

  async checkIfLoggedIn() {
    const accountId = await PersistentStore.getAccountId();
    const isOperator = AccountStore.getIsOperator();

    if (accountId && accountId !== '-1') {
      if (Platform.OS !== 'web') {
        this.context.actions.changeRootRoute(AppRoutes.AppProductTour);
      } else if (isOperator) {
        this.context.actions.changeRootRoute(AppRoutes.Operator);
      } else {
        this.context.actions.changeRootRoute(AppRoutes.App);
      }

      return true;
    }

    return false;
  }

  async webLoginAuth0() {
    await getWebAuth0Client().loginWithPopup();
    const refreshToken = await getWebAuth0Client().getTokenSilently();
    const user = await getWebAuth0Client().getUser();

    if (user) {
      const accessToken = await getWebAuth0Client().getTokenSilently();
      const idInfo = await getWebAuth0Client().getIdTokenClaims();

      await authStore.storeSession({
        accessToken,
        idToken: idInfo.__raw,
        expiresIn: idInfo.exp,
        tokenType: 'Bearer',
        scope: undefined,
        refreshToken: 'web',
      });

      const account = await ActionsFactory.getAccountActions().loginLegacy();
      await WelcomeAuth0Screen.onLoginSuccess(
        account,
        this.context,
        undefined,
        this.props.snack,
      );
    }
  }

  async handleClick() {
    const Auth0 = getMobileAuth0Client();
    const env = store.getState().settings.env;

    try {
      const credentials = await Auth0.webAuth.authorize(
        {
          scope: 'openid offline_access profile email',
          audience: Settings.auth0[env].audience,
          organization: Settings.auth0[env].orgIdAuth0,
        },
        {
          ephemeralSession: true,
        },
      );

      if (credentials) {
        const {snack} = this.props;

        await authStore.storeSession(credentials);

        NavActions.replace(AppRoutes.Redirect, {
          context: this.context,
          initialTabRoute: undefined,
          snack,
        });
      }
    } catch (error) {
      // no error user cancelled
      if (error && error.error.indexOf('user_cancelled') >= 0) {
        return;
      }



      const guid = await uuid.getRandomUUID();

      Events.Error.trackEvent(
        'Exception',
        'Login:Error',
        error.toString(),
        guid,
      );

      alertError('Unable to login at this time, please try again later.');
    }
  }

  static async onLoginSuccess(
    account: any,
    context: ScreenProps,
    initialTabRoute?: AppRoutes,
    snack?: SnackAction,
  ) {
    DealRepository.clearDeals();

    if (!AccountStore.isDemo()) {
      OneSignal.sendTags({
        email: account.data.email,
        mka: account.data.id,
        org: account.data.org,
        location: account.data.location,
        region: account.data.region,
        city: account.data.city,
        zip: account.data.zip,
        currency: account.data.currency,
        env: AccountStore.getEnvironment(),
      });
      OneSignal.setExternalUserId(account.data.id);
    }

    Events.AccountLogin.trackEvent(
      DeviceInfo.getModel(),
      account.data.region,
      account.data.currency,
    );

    if (snack) {
      context.actions.changeRootRoute(AppRoutes.App, initialTabRoute, {
        nestedNames: [AppRoutes.Inbox],
        name: AppRoutes.SnackDetail,
        params: {snackLocationId: snack.locationId, ...snack},
      });
    } else if (Platform.OS !== 'web') {
      context.actions.changeRootRoute(
        AppRoutes.AppProductTour,
        initialTabRoute,
      );
    } else {
      context.actions.changeRootRoute(AppRoutes.App, initialTabRoute);
    }

    SyncHelper.performSyncWithServer();
    ActionsFactory.getAccountActions().refreshMessages();
  }

  close() {
    NavActions.pop();
  }

  render() {
    if (Platform.OS === 'web') {
      return this.renderWeb();
    }

    return (
      <BackSubheader title={Localized.Labels.welcome_back}>
        <TouchableWithoutFeedback
          accessible={false}
          accessibilityLabel="Dismiss Keyboard"
          onPress={() => Keyboard.dismiss()}
        >
          <SafeAreaView style={styles.view}>
            <FocusAwareStatusBar
              barStyle={UIManager.getLoginStatusBarStyle()}
              backgroundColor={Styles.loginBackground}
            />
            <DeepLinkHandler />
            <CreateAccountPrivacyPolicyHandler
              ref={(privacyHandler) => {
                this.privacyHandler = privacyHandler;
              }}
            />
            <View style={styles.logoContainer} accessibilityLabel="365Pay Logo">
              <Logo />
            </View>
            <KeyboardAvoidingView style={styles.container} behavior="height">
              <ScrollView
                style={styles.content}
                keyboardDismissMode="interactive"
                automaticallyAdjustContentInsets={false}
                keyboardShouldPersistTaps="handled"
              >
                <RoundedButton
                  buttonType={ButtonType.normal}
                  containerStyle={styles.topButton}
                  accessibilityLabel="Login"
                  onPress={this.handleClick}
                  text={Localized.Buttons.sign_in}
                  textStyle={styles.textStyle}
                />
                <RoundedButton
                  buttonType={ButtonType.outline}
                  containerStyle={[
                    styles.topButton,
                    {marginTop: Styles.Spacing.m4},
                  ]}
                  accessibilityLabel="Operator"
                  onPress={this.handleLoginAVLive}
                  text={Localized.Buttons.operator}
                  textStyle={styles.textStyle}
                />
              </ScrollView>
              <AVText
                style={styles.versionText}
                accessibilityLabel="Version Number"
              >
                {this.state.fullVersion}
              </AVText>
            </KeyboardAvoidingView>
          </SafeAreaView>
        </TouchableWithoutFeedback>
      </BackSubheader>
    );
  }

  renderWeb() {
    return (
      <BackSubheader title={Localized.Labels.welcome_back}>
        <CreateAccountPrivacyPolicyHandler
          ref={(privacyHandler) => {
            this.privacyHandler = privacyHandler;
          }}
        />
        <View
          style={styles.logoContainer}
          accessibilityLabel="365Pay Logo"
          nativeID="365Pay Logo"
        >
          <Logo />
        </View>
        <KeyboardAvoidingView style={styles.container} behavior="height">
          <ScrollView
            style={[styles.content, Styles.Style.maxWidthContainer]}
            keyboardDismissMode="interactive"
            automaticallyAdjustContentInsets={false}
            keyboardShouldPersistTaps="handled"
          >
            <AVText
              style={styles.browserMessage}
              accessibilityLabel="Chrome Preferred Message"
            >
              {Localized.Labels.browser_version_notice}
            </AVText>
            <RoundedButton
              buttonType={ButtonType.normal}
              containerStyle={styles.topButton}
              accessibilityLabel="Login"
              onPress={this.webLoginAuth0}
              text={Localized.Buttons.sign_in}
              textStyle={styles.textStyle}
            />
          </ScrollView>
          <AVText
            style={styles.versionText}
            accessibilityLabel="Version Number"
          >
            {this.state.fullVersion}
          </AVText>
        </KeyboardAvoidingView>
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  browserMessage: {
    fontSize: Styles.Fonts.f0,
    fontWeight: 'bold',
    marginTop: Styles.Spacing.m2,
  },
  btnTextLight: {
    color: Styles.auxColor,
    fontSize: Styles.Fonts.f1,
    fontWeight: 'bold',
    marginVertical: Styles.Spacing.m1,
    textAlign: 'center',
  },
  container: {
    backgroundColor,
    flex: 1,
  },
  content: {
    flex: 1,
    marginBottom: Styles.Spacing.m3,
    paddingHorizontal: Styles.Spacing.m4,
  },
  instructions: {
    color: textColor,
    fontSize: Styles.Fonts.f3,
    fontWeight: 'bold',
  },
  logoContainer: {
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor,
    height: Styles.Heights.h3,
    justifyContent: 'center',
    // paddingTop: Styles.Spacing.m3,
    flex: 1,
  },
  topButton: {
    marginTop: Styles.Spacing.m3,
    alignSelf: 'auto',
    paddingVertical: Styles.Spacing.m1,
  },
  textStyle: {
    flex: 1,
    textAlign: 'center',
    fontSize: Styles.Fonts.f1,
  },
  versionText: {
    alignSelf: 'flex-start',
    color: Styles.auxColor,
    fontSize: Styles.Fonts.f0,
    marginBottom: Styles.Spacing.m3,
    marginLeft: Styles.Spacing.m4,
  },
  view: {
    backgroundColor,
    flex: 1,
  },
});

export default withForwardedNavigationParams()(WelcomeAuth0Screen);
